import * as React from 'react';
import moment from 'moment';
import theme from "../../theme";
import { makeStyles } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { getUserActivityLogsAction } from "../../containers/dashboard/action";
import { Pagination } from '@material-ui/lab';
import CommonTable from "../table";
import CommonDropDown from '../dropdown';
import CommonInput from "../input";
import { getCompaniesAction } from '../../containers/login/action';
import MarkdownExpand from '../markdown';
import { flatten } from 'flat';

let companyOptions = [];

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: theme.colors.White,
    fontFamily: theme.fontFamily.MARKPRO,
    backgroundColor: theme.colors.White,
    width: "100%",
    minHeight: "calc(100vh - 175px)",
    padding: "45px 90px",
    marginLeft: "200px",
    overflowX: "hidden"
  },
  searchTab: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "nowrap",
    width: "100%",
    minWidth: "380px",
    padding: "0.75rem 1rem",
    gap: "1rem",
    boxSizing: "border-box",
  },
  companyInput: {
    width: "50%",
    minWidth: "380px",
  },
  contextCell: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    lineHeight: 1.35,
  },
  contextLine: {
    color: theme.colors.Violet,
    fontSize: "12px",
    wordBreak: "break-word",
  },
  contextLabel: {
    fontFamily: theme.fontFamily.MARKPROBOLD,
    marginRight: "6px",
  },
  metadataSummary: {
    cursor: "pointer",
    color: theme.colors.DodgerBlue,
    fontSize: "12px",
    width: "fit-content",
  },
  metadataBlock: {
    background: "#F9FBFF",
    border: "1px solid #E6EFFC",
    borderRadius: "8px",
    padding: "8px 10px",
    fontSize: "11px",
    maxHeight: "180px",
    overflow: "auto",
    wordBreak: "break-word",
  },
  metadataSection: {
    marginTop: "8px",
  },
  metadataSectionTitle: {
    color: theme.colors.DodgerBlue,
    fontSize: "11px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    marginBottom: "6px",
    textTransform: "capitalize",
  },
  metadataList: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  metadataItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingBottom: "6px",
    borderBottom: "1px dashed #D8E6FA",
  },
  metadataItemKey: {
    fontSize: "11px",
    color: "#4F7BC0",
    fontFamily: theme.fontFamily.MARKPROBOLD,
  },
  metadataItemValue: {
    fontSize: "11px",
    color: theme.colors.Violet,
  }
}));

function getActionDisplay(data) {
  switch (data) {
    case "FOLLOW_UP":
      return "FOLLOW UP"
    default:
      return data;
  }
}

export default function UserActivityLogsPage() {
  const classes = useStyles();

  const dispatch = useDispatch();
  const dashboardReducer = useSelector((state) => state.dashboardReducer);
  const loginReducer = useSelector((state) => state.loginReducer);
  const [activityFilters, setActivityFilters] = React.useState({ page: 1, page_size: 10 })
  const getUserActivityLogsActionMethod = React.useCallback
    ((data) => dispatch(getUserActivityLogsAction(data)),
      [dispatch]);


  const getCompaniesActionMethod = React.useCallback(() => {
    return dispatch(getCompaniesAction())
  }, [dispatch])

  const user = JSON.parse(localStorage.getItem("user"));


  React.useEffect(() => {
    if (user?.is_super)
      getCompaniesActionMethod();
  }, [getCompaniesActionMethod])

  React.useEffect(() => {
    if (loginReducer?.companyOptions?.length) {
      companyOptions = [];
      for (let i = 0; i < loginReducer?.companyOptions.length; i++) {
        const each = loginReducer?.companyOptions[i];
        if (
          companyOptions.findIndex(
            (rec) => rec.value && rec.value === each.id
          ) < 0
        )
          companyOptions.push({ value: each.id, label: each.name });
      }
    }
  }, [loginReducer?.companyOptions])

  React.useEffect(() => {
    getUserActivityLogsActionMethod(activityFilters)
  }, [
    activityFilters
  ])

  const data = dashboardReducer?.usersActivityLogs?.res?.data

  const toReadableLabel = React.useCallback((key = "") => {
    return key
      .replace(/\[(\d+)\]/g, " $1 ")
      .replace(/[._]/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^./, (char) => char.toUpperCase());
  }, []);

  const toReadableValue = React.useCallback((value) => {
    if (value === null || value === undefined || value === "") return "N/A";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }
    return String(value);
  }, []);

  const getSectionEntries = React.useCallback((sectionValue) => {
    if (sectionValue === null || sectionValue === undefined) return [];
    if (typeof sectionValue !== "object") {
      return [{ key: "value", value: sectionValue }];
    }

    const flattenedSection = flatten(sectionValue, { safe: true });
    return Object.keys(flattenedSection).map((key) => ({
      key,
      value: flattenedSection[key],
    }));
  }, []);

  const renderMetadataContext = React.useCallback((record) => {
    const metadata = record?.metadata || {};
    const paramsEntries = getSectionEntries(metadata?.params);
    const dataEntries = getSectionEntries(metadata?.data);
    const hasPayload = paramsEntries.length > 0 || dataEntries.length > 0;

    return (
      <div className={classes.contextCell}>
        <div className={classes.contextLine}>
          <span className={classes.contextLabel}>Method:</span>
          {metadata?.method || "N/A"}
        </div>
        <div className={classes.contextLine}>
          <span className={classes.contextLabel}>IP:</span>
          {record?.ip_address || "N/A"}
        </div>
        <div className={classes.contextLine}>
          <span className={classes.contextLabel}>Agent:</span>
          {record?.user_agent || "N/A"}
        </div>
        {hasPayload ? (
          <details>
            <summary className={classes.metadataSummary}>View request details</summary>
            <div className={classes.metadataBlock}>
              <div className={classes.metadataSection}>
                <div className={classes.metadataSectionTitle}>URL parameters</div>
                {paramsEntries.length ? (
                  <div className={classes.metadataList}>
                    {paramsEntries.map((entry) => (
                      <div key={`params-${entry.key}`} className={classes.metadataItem}>
                        <span className={classes.metadataItemKey}>{toReadableLabel(entry.key)}</span>
                        <span className={classes.metadataItemValue}>{toReadableValue(entry.value)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={classes.contextLine}>No URL parameters</div>
                )}
              </div>
              <div className={classes.metadataSection}>
                <div className={classes.metadataSectionTitle}>Request body</div>
                {dataEntries.length ? (
                  <div className={classes.metadataList}>
                    {dataEntries.map((entry) => (
                      <div key={`data-${entry.key}`} className={classes.metadataItem}>
                        <span className={classes.metadataItemKey}>{toReadableLabel(entry.key)}</span>
                        <span className={classes.metadataItemValue}>{toReadableValue(entry.value)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={classes.contextLine}>No request body</div>
                )}
              </div>
            </div>
          </details>
        ) : (
          <div className={classes.contextLine}>
            <span className={classes.contextLabel}>Payload:</span>
            None
          </div>
        )}
      </div>
    );
  }, [
    classes.contextCell,
    classes.contextLabel,
    classes.contextLine,
    classes.metadataBlock,
    classes.metadataItem,
    classes.metadataItemKey,
    classes.metadataItemValue,
    classes.metadataList,
    classes.metadataSection,
    classes.metadataSectionTitle,
    classes.metadataSummary,
    getSectionEntries,
    toReadableLabel,
    toReadableValue
  ]);

  const activityLogColumns = [
    {
      Header: "User",
      accessor: "user",
      HeaderCell: (data) => (<header>{data}</header>)
    },
    {
      Header: "Action",
      accessor: "action",
      Cell: (data) => (<p>{getActionDisplay(data)}</p>)
    },
    {
      Header: "Date",
      accessor: "created_at",
      Cell: (data) => (<p>{moment(data).format("MMM Do YY [at] h:mm a")}</p>)
    },
    {
      Header: "Description",
      accessor: "description",
      Cell: (data, record, index) => (<MarkdownExpand text={data} limit={100} />)
    },
    ...(user?.is_super ? [{
      Header: "Log Context",
      accessor: "metadata",
      Cell: (data, record) => renderMetadataContext(record)
    }] : []),
    {
      Header: "Company",
      accessor: "company"
    }
  ];

  return <div className={classes.root}>
    {/* <header>User Activity Logs Page</header> */}
    <div className={classes.searchTab}>
      {user?.is_super && <CommonDropDown
        placeholder={"Select Company"}
        options={companyOptions}
        value={companyOptions.find((v) => activityFilters?.company_id === v.value)}
        onChange={(data) => {
          if (data?.value) {
            setActivityFilters((prev) => ({ ...prev, company_id: data?.value }));
          } else {
            const { company_id, ...others } = { ...activityFilters, company_id: null };
            setActivityFilters(others);
          }

        }}
        isClearable={true}
        className={classes.companyInput}
      />}
      <CommonInput
        label={"Search User"}
        value={activityFilters?.search}
        onChange={
          (e) => {
            setActivityFilters((prev) => ({ ...prev, search: e.target.value }))
          }
        }
        placeholder={"Type in firstname, last_name or email ..."}
      />
    </div>
    {/* <ThreeDotsLoading showLoader={dashboardReducer?.usersActivityLogs?.isLoading} /> */}
    <CommonTable columns={activityLogColumns}
      data={dashboardReducer?.usersActivityLogs?.res?.data}
      loading={dashboardReducer?.usersActivityLogs?.isLoading}
    />
    {data?.length ? (
      <div className={"pagination"}>
        <Pagination
          page={activityFilters?.page}
          count={Math.ceil((dashboardReducer?.usersActivityLogs?.res?.count || 0) / activityFilters.page_size)}
          showFirstButton
          showLastButton
          onChange={(event, value) => {
            setActivityFilters((prev) => ({ ...prev, page: value }))
          }}
        />
      </div>
    ) : null}
  </div>
}